import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { isDomesticOfficialSeedance } from '../../../utils/domesticSeedanceCapabilities';
import ReactDOM from "react-dom";
import { Upload, Trash2, Wand2, RefreshCw, Maximize2, Loader2, Lightbulb, Settings2, X, Play, PenTool, History, Download, Grid3X3, FolderOpen, Pencil, Scissors, ZoomIn, SquareDashed, RemoveFormatting, Drama, Mountain, Wrench, Clapperboard, Palette } from "lucide-react";
import ResourceSelector from "../../resource/ResourceSelector";
import SketchEditorModal from "../../common/SketchEditorModal";
import { VIDEO_MODELS } from "../common/Constants";
import VideoSettingsPanel from "../common/VideoSettingsPanel";
import HistoryPanel from "../common/HistoryPanel";
import FrameCaptureModal from "../common/FrameCaptureModal";
import MosaicEditorModal from "../common/MosaicEditorModal";
import { useTheme } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { uploadFileToCos } from "../../../utils/fileUtils";
import { serializeEditor, createMentionChipSmall, renderPromptToEditor } from "../../../utils/mentionUtils";
import { syncGeneratedImageForRow } from "../../../utils/storyboardImageSync";
import { submitEraseSubtitle, startPostProcessPolling } from "../../../services/videoService";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";
import {
  getProjectVideoProvider,
  getVideoProviderLabel,
  isSeedanceVideoProvider,
  supportsVideoTaskCancel,
} from "../../../utils/projectRuntime";
import { isWanxVideoModel, normalizeWanxVideoModel } from "../../../utils/wanxVideoCapabilities";

const isGuaLikeModel = isSeedanceVideoProvider;

// ── @ 菜单大图预览（独立组件，state 变化不影响父组件） ──
const VpMentionPreview = React.memo(({ url, anchorRect }) => {
  if (!url) return null;
  if (!anchorRect) return null;
  const previewWidth = Math.min(400, window.innerWidth * 0.3) + 20;
  const spaceRight = window.innerWidth - anchorRect.right;
  const pos = spaceRight >= previewWidth
    ? { left: anchorRect.right + 8, right: 'auto' }
    : { right: window.innerWidth - anchorRect.left + 8, left: 'auto' };
  return ReactDOM.createPortal(
    <div className="fixed z-[400] pointer-events-none" style={{ ...pos, bottom: window.innerHeight - anchorRect.bottom }}>
      <img src={url} alt="" className="rounded-xl shadow-2xl" style={{
        maxWidth: 'min(400px, 30vw)', maxHeight: 'min(400px, 40vh)', width: 'auto', height: 'auto', objectFit: 'contain',
        backgroundColor: 'color-mix(in srgb, var(--color-bg-overlay) 95%, transparent)', border: '1px solid var(--color-border-primary)', padding: '4px',
      }} />
    </div>,
    document.body
  );
});

// ── 工具栏图标按钮（统一 hover 高亮 + tooltip） ──
const IconBtn = React.memo(({ onClick, disabled, color, activeColor, active, label, children, className = "" }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`group/tip relative production-icon-button flex items-center justify-center p-1 transition-colors duration-150 ${className}`}
      style={{ color: active ? (activeColor || color) : color }}
      aria-label={label}
    >
      <span className="block">
        {children}
      </span>
      <span
        className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2 py-1 rounded text-2xs font-medium whitespace-nowrap pointer-events-none opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-50"
        style={{ backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
      >
        {label}
      </span>
    </button>
  );
});

const ShotVisuals = ({
  scene,
  index,
  rows,
  setRows,
  currentRatio,
  handleChange,
  handleGenerateImage,
  handleGenerateImageBatch,
  handleGenerateVideo,
  stopVideoGeneration,
  stopImageGeneration,
  handleAutoVideoPrompt,
  handleSketchUpload,
  handleLocalImageUpload,
  handleLocalVideoUpload,
  onOpenWorkstation,
  openVideoSettingsIndex,
  setOpenVideoSettingsIndex,
  projectId,
  episodeId,
  onOpenMultiShot,
  handleInsertRowAndSave,
  triggerSave,
  characterOptions,
  sceneOptions,
  propOptions,
  isFirstLastFrameMode,
  videoEnabled = true,
}) => {
  const sketchInputRef = useRef(null);
  const imageUploadRef = useRef(null);
  const videoUploadRef = useRef(null);
  const modelBtnRef = useRef(null);
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [showImageHistory, setShowImageHistory] = useState(false);
  const [showVideoHistory, setShowVideoHistory] = useState(false);
  const [vpPreviewUrl, setVpPreviewUrl] = useState(null); // @ 菜单预览 URL
  const [vpPreviewRect, setVpPreviewRect] = useState(null); // @ 菜单预览锚点位置
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [showVideoResourceSelector, setShowVideoResourceSelector] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);
  const [showSketchEditor, setShowSketchEditor] = useState(false);
  const [showFrameCapture, setShowFrameCapture] = useState(false);
  const [showMosaicEditor, setShowMosaicEditor] = useState(false);

  // 出场动画：历史记录弹层 / 帧截取 / 打码弹窗延迟卸载
  const imageHistoryAnim = useDelayedUnmount(showImageHistory, 150);
  const videoHistoryAnim = useDelayedUnmount(videoEnabled && showVideoHistory, 150);
  const frameCaptureAnim = useDelayedUnmount(videoEnabled && showFrameCapture && !!scene.generated_video, 150);
  const mosaicEditorAnim = useDelayedUnmount(showMosaicEditor && !!scene.generated_image, 150);
  const videoSettingsAnim = useDelayedUnmount(videoEnabled && openVideoSettingsIndex === index, 150);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [isErasingSubtitle, setIsErasingSubtitle] = useState(false);
  const visibleLastFrameImage = isFirstLastFrameMode
    ? (scene.last_frame_image || scene.lastFrameImage)
    : "";

  const replaceGeneratedImage = useCallback((nextUrl, options = {}) => {
    if (!nextUrl || !setRows) return;
    const oldUrl = options.oldUrl ?? scene.generated_image;
    setRows((prev) => {
      if (!Array.isArray(prev)) return prev;
      const n = [...prev];
      const rowIndex = scene.id != null
        ? n.findIndex((row) => row.id === scene.id)
        : index;
      if (rowIndex < 0 || rowIndex >= n.length) return prev;
      n[rowIndex] = syncGeneratedImageForRow(n[rowIndex], nextUrl, {
        oldUrl,
        source: options.source,
        model: options.model,
        addHistory: options.addHistory,
        ensureCandidate: options.ensureCandidate,
        clearLastFrame: options.clearLastFrame ?? true,
      });
      return n;
    });
    triggerSave?.();
  }, [scene.id, scene.generated_image, index, setRows, triggerSave]);

  // 超分
  const handleUpscale = useCallback(async () => {
    if (!scene.generated_image || isUpscaling) return;
    const sourceUrl = scene.generated_image;
    setIsUpscaling(true);
    toast.info("超分任务已提交");
    try {
      const submitRes = await authenticatedFetch(`${API_HOST}/api/super-resolution/submit`, {
        method: "POST",
        body: JSON.stringify({
          imageUrl: sourceUrl,
          resolution: "4k",
          scale: 50,
          projectId: projectId ? Number(projectId) : null,
          episodeId: episodeId ? Number(episodeId) : null,
          shotId: scene.id ?? null,
          source: "storyboard_super_resolution",
          resourceTypeCode: "storyboard_image",
        }),
      });
      if (!submitRes?.success || !submitRes?.taskId) throw new Error(submitRes?.error || "提交失败");

      const taskId = submitRes.taskId;
      const poll = async () => {
        try {
          const res = await authenticatedFetch(`${API_HOST}/api/super-resolution/query?taskId=${taskId}`);
          if (res?.status === "success" && res?.imageUrls?.length > 0) {
            replaceGeneratedImage(res.imageUrls[0], {
              oldUrl: sourceUrl,
              source: "upscale",
              model: "super-resolution",
            });
            toast.success("超分完成");
            setIsUpscaling(false);
          } else if (res?.status === "failed") {
            toast.error("超分失败: " + (res?.error || ""));
            setIsUpscaling(false);
          } else {
            setTimeout(poll, 3000);
          }
        } catch {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      toast.error("超分失败: " + err.message);
      setIsUpscaling(false);
    }
  }, [scene.generated_image, scene.id, isUpscaling, toast, replaceGeneratedImage, projectId, episodeId]);

  // 去字幕
  const handleEraseSubtitle = useCallback(async () => {
    if (!videoEnabled || isErasingSubtitle) return;

    const videoUrl = scene.original_video_url;
    if (!videoUrl) {
      toast.error('缺少原始方舟视频 URL，仅支持 GuaVideo 24小时内生成的视频');
      return;
    }

    // 捕获当前视频，用于后续推入历史（闭包里固定下来）
    const shotId = scene.id;
    const previousVideoUrl = scene.generated_video;
    const previousVideoPrompt = scene.video_prompt;
    const previousVideoModel = scene.video_model || scene.video_sub_model || 'guavideo';

    setIsErasingSubtitle(true);
    toast.info('去字幕任务已提交，正在处理...');
    try {
      const response = await submitEraseSubtitle({ videoUrl, shotId });
      const taskId = response.taskId;
      startPostProcessPolling('erase-subtitle', taskId, {
        interval: 5000,
        shotId,
        onSuccess: (data) => {
          const newUrl = data.videoUrl;
          // 原子地更新：把老视频归档进 history，new URL 作为当前视频；同时清掉 video_task_id/status 避免被保存流程的
          // "后端 polling 结果优先" 逻辑覆盖
          setRows?.((prev) => {
            const next = [...prev];
            const idx = next.findIndex(r => r.id === shotId);
            if (idx === -1) return prev;
            const current = next[idx];
            const history = Array.isArray(current.history_list) ? [...current.history_list] : [];

            // 先归档去字幕前的视频（如果还没在 history 末尾）
            if (previousVideoUrl && (!history.length || history[history.length - 1]?.url !== previousVideoUrl)) {
              history.push({
                type: 'video',
                url: previousVideoUrl,
                prompt: previousVideoPrompt,
                model: previousVideoModel,
                created_at: Date.now() - 1,
              });
            }
            // 再追加去字幕后的视频
            history.push({
              type: 'video',
              url: newUrl,
              prompt: previousVideoPrompt,
              model: previousVideoModel,
              source: 'erase_subtitle',
              created_at: Date.now(),
            });

            next[idx] = {
              ...current,
              generated_video: newUrl,
              // 去字幕后原始方舟 URL 不再适用（可能已过期），清掉避免再次去字幕用到错的源
              original_video_url: null,
              // 清掉旧任务关联，防止保存时被后端已成功的老任务结果覆盖
              video_task_id: null,
              video_task_status: 'success',
              history_list: history.slice(-10),
            };
            return next;
          });
          triggerSave?.();
          toast.success('去字幕完成');
          setIsErasingSubtitle(false);
        },
        onError: (msg) => {
          toast.error('去字幕失败: ' + msg);
          setIsErasingSubtitle(false);
        },
        onProgress: () => {},
      });
    } catch (err) {
      toast.error('提交去字幕失败: ' + err.message);
      setIsErasingSubtitle(false);
    }
  }, [scene.id, scene.original_video_url, scene.generated_video, scene.video_prompt, scene.video_model, scene.video_sub_model, isErasingSubtitle, setRows, toast, triggerSave, videoEnabled]);

  useEffect(() => {
    if (videoEnabled) return;
    setShowVideoHistory(false);
    setShowVideoResourceSelector(false);
    setShowFrameCapture(false);
    setVpPreviewUrl(null);
    setVpPreviewRect(null);
    setVpMention(null);
  }, [videoEnabled]);

  // 有候选图时自动展开
  useEffect(() => {
    if (scene.image_candidates?.length > 1) {
      setShowCandidates(true);
    }
  }, [scene.image_candidates?.length]);

  const handleRestore = (item) => {
      if (item.type === 'image') {
          replaceGeneratedImage(item.url, {
            source: 'restore',
            model: item.model || 'history',
            addHistory: false,
            ensureCandidate: false,
          });
          handleChange(index, 'image_prompt', item.prompt);
          setShowImageHistory(false);
      } else {
          handleChange(index, 'generated_video', item.url);
          handleChange(index, 'video_prompt', item.prompt);
          const historyProvider = String(item.provider || item.model || '').trim().toLowerCase();
          const historyModel = String(item.sub_model || item.actual_model || '').trim().toLowerCase();
          if (historyProvider === 'wanx' || isWanxVideoModel(historyProvider) || isWanxVideoModel(historyModel)) {
            const restoredWanxModel = normalizeWanxVideoModel(historyModel || historyProvider);
            handleChange(index, 'video_provider', 'wanx');
            handleChange(index, 'video_model', 'wanx');
            handleChange(index, 'video_sub_model', restoredWanxModel);
            handleChange(index, 'video_wan_mode', item.mode || 'first_frame');
            handleChange(index, 'video_wan_duration', item.duration ?? 5);
            handleChange(index, 'video_wan_resolution', item.resolution || '1080P');
            handleChange(index, 'video_wan_ratio', item.ratio || 'adaptive');
            handleChange(index, 'video_wan_audio', item.audio ?? (item.sound !== 'off'));
            handleChange(index, 'video_wan_watermark', item.watermark === true);
            handleChange(index, 'video_wan_prompt_extend', item.promptExtend !== false);
            handleChange(index, 'video_wan_seed', item.seed ?? null);
            handleChange(index, 'video_wan_media', Array.isArray(item.wanxMedia)
              ? item.wanxMedia.map((media) => ({ ...media }))
              : []);
          }
          setShowVideoHistory(false);
      }
  };

  // 下载图片
  const handleDownloadImage = () => {
    if (!scene.generated_image) return;
    const link = document.createElement('a');
    link.href = scene.generated_image;
    link.download = `shot_${scene.shot_number || index}_image.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 下载视频
  const handleDownloadVideo = () => {
    if (!scene.generated_video) return;
    const link = document.createElement('a');
    link.href = scene.generated_video;
    link.download = `shot_${scene.shot_number || index}_video.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 选择资源图片
  const handleSelectResource = (resource) => {
    if (resource.typeCode?.includes('video')) {
      handleChange(index, 'generated_video', resource.url);
    } else {
      replaceGeneratedImage(resource.url, {
        source: 'resource',
        model: 'resource',
      });
    }
  };

  const storedVideoProvider = scene.video_provider || scene.videoProvider || scene.video_model || scene.videoModel || "vidu";
  const storedModelId = isWanxVideoModel(storedVideoProvider)
    ? 'wanx'
    : storedVideoProvider;
  const projectVideoProvider = getProjectVideoProvider(projectId, "guavideo");
  const effectiveModelId = isGuaLikeModel(storedModelId) && !isDomesticOfficialSeedance(storedModelId) ? projectVideoProvider : storedModelId;
  const fallbackModel = VIDEO_MODELS?.[0] || { name: '加载中...', icon: Settings2 };
  const currentModel = VIDEO_MODELS?.find(m => m.id === effectiveModelId) || {
    ...fallbackModel,
    id: effectiveModelId,
    name: isGuaLikeModel(storedModelId) ? getVideoProviderLabel(effectiveModelId) : fallbackModel.name,
    tag: '',
  };
  const aspectRatio = currentRatio.replace(":", "/");
  const isGuaVideo = isGuaLikeModel(currentModel.id);
  const canCancelVideoTask = supportsVideoTaskCancel(effectiveModelId);

  // ── @ 提及 ──
  const vpEditorRef = useRef(null);
  const [vpMention, setVpMention] = useState(null); // { x, y, searchText, level, path, category, asset }
  const vpMentionIdxRef = useRef(0);
  const [vpMenuItems, setVpMenuItems] = useState([]);
  const [vpMenuLoading, setVpMenuLoading] = useState(false);
  const [vpRefNames, setVpRefNames] = useState(new Map()); // url → name 映射
  const vpMentionUploadRef = useRef(null);
  const vpMentionItemRefs = useRef([]);
  const vpMentionListRef = useRef(null); // @ 菜单容器 ref
  const vpAtNodeRef = useRef(null); // @ 所在的文本节点
  const vpAtIndexRef = useRef(-1); // @ 在文本节点中的 index

  const vpMentionSource = useMemo(() => {
    if (!isGuaVideo) return [];
    return (scene.video_gua_ref_images || []).map((url, i) => ({ id: `g-${i}`, name: `图片${i + 1}`, url }));
  }, [isGuaVideo, scene.video_gua_ref_images]);

  const vpFiltered = useMemo(() => {
    if (!vpMention) return [];
    const q = vpMention.searchText || '';
    if (vpMention.level === 'root') {
      const existingItems = isGuaVideo
        ? (scene.video_gua_ref_images || []).map((url, i) => ({
            id: `existing-${i}`, icon: null, imageUrl: url, url,
            label: vpRefNames.get(url) || `图片${i + 1}`,
            action: 'select-existing',
          }))
        : [];
      const rootItems = [
        ...existingItems,
        { id: 'resource', icon: '📁', label: '资源管理器', action: 'navigate', nextLevel: 'source' },
        { id: 'upload', icon: '📤', label: '本地上传', action: 'upload' },
      ];
      return q ? rootItems.filter(i => i.label.toLowerCase().includes(q)) : rootItems;
    }
    if (['source', 'assets', 'variants'].includes(vpMention.level)) {
      return q ? vpMenuItems.filter(i => i.label.toLowerCase().includes(q)) : vpMenuItems;
    }
    return vpMentionSource.filter(m => !q || m.name.toLowerCase().includes(q));
  }, [vpMention, vpMentionSource, vpMenuItems, isGuaVideo, scene.video_gua_ref_images, vpRefNames]);

  const vpFilteredRef = useRef(vpFiltered);
  vpFilteredRef.current = vpFiltered;

  // 出场动画：@ 提及菜单延迟卸载（缓存 vpMention 供退场渲染）
  const vpMenuOpen = !!(vpMention && (vpFiltered.length > 0 || vpMenuLoading));
  const vpMenuAnim = useDelayedUnmount(vpMenuOpen, 150);
  const lastVpMentionRef = useRef(null);
  if (vpMention) lastVpMentionRef.current = vpMention;

  const getVpSerialized = useCallback(() => serializeEditor(vpEditorRef.current), []);

  // 更新 @ 菜单选中项（DOM 操作高亮，state 只更新预览 URL）
  const updateVpMentionIndex = useCallback((newIndex) => {
    const oldIndex = vpMentionIdxRef.current;
    vpMentionIdxRef.current = newIndex;
    const oldItem = vpMentionItemRefs.current[oldIndex];
    const newItem = vpMentionItemRefs.current[newIndex];
    if (oldItem) {
      oldItem.style.backgroundColor = 'transparent';
      oldItem.style.color = 'var(--color-text-secondary)';
    }
    if (newItem) {
      newItem.style.backgroundColor = 'var(--color-purple-light)';
      newItem.style.color = 'var(--color-purple-primary)';
      newItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const imgUrl = newItem.dataset.imageUrl;
      setVpPreviewUrl(imgUrl || null);
      setVpPreviewRect(imgUrl ? (vpMentionListRef.current?.getBoundingClientRect() ?? null) : null);
    } else {
      setVpPreviewUrl(null);
    }
  }, []);

  useEffect(() => {
    if (!vpMention) setVpPreviewUrl(null);
  }, [vpMention]);

  // 同步参考图变化到 chips（只做删除同步，不再覆盖 name）
  useEffect(() => {
    if (!videoEnabled) return;
    if (!vpEditorRef.current) return;
    const chips = vpEditorRef.current.querySelectorAll('[data-mention]');
    if (chips.length === 0) return;
    const urlSet = new Set(vpMentionSource.map(m => m.url));
    let changed = false;
    chips.forEach(chip => {
      if (!urlSet.has(chip.dataset.url)) { chip.remove(); changed = true; }
    });
    if (changed) {
      const newPrompt = getVpSerialized();
      console.log('[ShotVisuals sync] chip 被删除，更新 video_prompt:', newPrompt);
      handleChange(index, 'video_prompt', newPrompt);
    }
  }, [vpMentionSource, videoEnabled, getVpSerialized, handleChange, index]);

  const vpEditingRef = useRef(false); // 标记自己是否正在编辑

  const handleVpInput = useCallback(() => {
    vpEditingRef.current = true;
    handleChange(index, 'video_prompt', getVpSerialized());

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { setVpMention(null); return; }
    const r = sel.getRangeAt(0);
    if (r.startContainer.nodeType !== Node.TEXT_NODE) { setVpMention(null); return; }
    const before = r.startContainer.textContent.substring(0, r.startOffset);
    const at = before.lastIndexOf('@');
    if (at !== -1) {
      const after = before.substring(at + 1);
      if (!after.includes(' ') && !after.includes('\n')) {
        const rect = vpEditorRef.current.getBoundingClientRect();
        setVpMention({ x: rect.left + 10, y: rect.top, searchText: after.toLowerCase(), level: 'root', path: [] });
        vpAtNodeRef.current = r.startContainer;
        vpAtIndexRef.current = at;
        vpMentionIdxRef.current = 0;
        setVpMenuItems([]);
        return;
      }
    }
    setVpMention(null);
  }, [getVpSerialized, index, handleChange]);

  const handleVpSelect = useCallback((item) => {
    if (!vpMention || !vpEditorRef.current) return;

    const atNode = vpAtNodeRef.current;
    const atIdx = vpAtIndexRef.current;

    if (!atNode || atIdx < 0 || !atNode.parentNode) {
      setVpMention(null);
      vpAtNodeRef.current = null;
      vpAtIndexRef.current = -1;
      return;
    }

    // 只删除 @ + 搜索文字，不多删
    const searchLen = (vpMention.searchText || '').length;
    const endOffset = Math.min(atIdx + 1 + searchLen, (atNode.textContent || '').length);

    const dr = document.createRange(); dr.setStart(atNode, atIdx); dr.setEnd(atNode, endOffset); dr.deleteContents();
    const chip = createMentionChipSmall(item.name, item.url, 'var(--color-purple-primary)', item.category || '');
    const afterNode = atNode.textContent.length > atIdx ? atNode.splitText(atIdx) : null;
    if (afterNode) {
      atNode.parentNode.insertBefore(chip, afterNode);
    } else if (atNode.parentNode) {
      atNode.parentNode.insertBefore(chip, atNode.nextSibling);
    } else {
      vpEditorRef.current.appendChild(chip);
    }
    const sp = document.createTextNode('\u00A0'); chip.after(sp);
    const sel = window.getSelection();
    const nr = document.createRange(); nr.setStartAfter(sp); nr.collapse(true); sel.removeAllRanges(); sel.addRange(nr);
    vpAtNodeRef.current = null;
    vpAtIndexRef.current = -1;
    setVpMention(null);
    handleChange(index, 'video_prompt', getVpSerialized());
  }, [vpMention, index, handleChange, getVpSerialized]);

  // ── @ 多级菜单：导航 ──
  const handleVpMenuNavigate = useCallback(async (item) => {
    if (!vpMention) return;
    const prev = vpMention;

    if (item.action === 'upload') {
      vpMentionUploadRef.current?.click();
      return;
    }

    if (item.action === 'navigate') {
      const nextLevel = item.nextLevel;
      const nextPath = [...prev.path, item.label];
      setVpMention({ ...prev, level: nextLevel, path: nextPath, category: item.category, asset: item.asset, searchText: '' });
      vpMentionIdxRef.current = 0;
      setVpMenuLoading(true);

      try {
        let items = [];
        if (nextLevel === 'source') {
          items = [
            { id: 'character', icon: Drama, label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
            { id: 'scene', icon: Mountain, label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
            { id: 'prop', icon: Wrench, label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
            { id: 'storyboard', icon: Clapperboard, label: '分镜', action: 'navigate', nextLevel: 'assets', category: 'storyboard' },
            { id: 'imageAgent', icon: Palette, label: '图片Agent', action: 'navigate', nextLevel: 'assets', category: 'imageAgent' },
          ];
        } else if (nextLevel === 'assets') {
          const cat = item.category;
          if (cat === 'character') {
            const data = await authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}`);
            items = (data || []).filter(c => c.imageUrl).map(c => ({
              id: c.id, label: c.name, imageUrl: c.imageUrl, url: c.imageUrl,
              sourceType: 'characters',
              sourceAssetId: c.id,
              guaVideoAssetId: c.guaVideoAssetId,
              guaVideoAssetStatus: c.guaVideoAssetStatus,
              engGuaVideoAssetId: c.engGuaVideoAssetId,
              engGuaVideoAssetStatus: c.engGuaVideoAssetStatus,
              guaOminiAssetId: c.guaOminiAssetId,
              guaOminiAssetStatus: c.guaOminiAssetStatus,
              dreaminaProxyAssetId: c.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: c.dreaminaProxyAssetStatus,
              voiceSampleUrl: c.voiceSampleUrl,
              voiceSampleName: c.voiceSampleName,
              voiceBoundAt: c.voiceBoundAt,
              action: (c.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: c,
            }));
          } else if (cat === 'scene') {
            const data = await authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}`);
            items = (data || []).filter(s => s.imageUrl).map(s => ({
              id: s.id, label: s.name, imageUrl: s.imageUrl, url: s.imageUrl,
              sourceType: 'scenes',
              sourceAssetId: s.id,
              guaVideoAssetId: s.guaVideoAssetId,
              guaVideoAssetStatus: s.guaVideoAssetStatus,
              engGuaVideoAssetId: s.engGuaVideoAssetId,
              engGuaVideoAssetStatus: s.engGuaVideoAssetStatus,
              guaOminiAssetId: s.guaOminiAssetId,
              guaOminiAssetStatus: s.guaOminiAssetStatus,
              dreaminaProxyAssetId: s.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: s.dreaminaProxyAssetStatus,
              action: (s.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: s,
            }));
          } else if (cat === 'prop') {
            const data = await authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}`);
            items = (data || []).filter(p => p.imageUrl).map(p => ({
              id: p.id, label: p.name, imageUrl: p.imageUrl, url: p.imageUrl,
              sourceType: 'props',
              sourceAssetId: p.id,
              guaVideoAssetId: p.guaVideoAssetId,
              guaVideoAssetStatus: p.guaVideoAssetStatus,
              engGuaVideoAssetId: p.engGuaVideoAssetId,
              engGuaVideoAssetStatus: p.engGuaVideoAssetStatus,
              guaOminiAssetId: p.guaOminiAssetId,
              guaOminiAssetStatus: p.guaOminiAssetStatus,
              dreaminaProxyAssetId: p.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: p.dreaminaProxyAssetStatus,
              action: (p.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: p,
            }));
          } else if (cat === 'storyboard') {
            const data = await authenticatedFetch(`${API_HOST}/api/storyboard/load?episodeId=${episodeId}`);
            items = (data || []).filter(s => s.generated_image).map((s, i) => ({
              id: `shot-${i}`, label: `镜头${s.shot_number || i + 1}`, imageUrl: s.generated_image, url: s.generated_image,
              action: 'select',
            }));
          } else if (cat === 'imageAgent') {
            const data = await authenticatedFetch(`${API_HOST}/api/resource/query`, {
              method: 'POST', body: JSON.stringify({ projectId, type: 'IMAGE', page: 0, size: 50 }),
            });
            const list = data?.content || data || [];
            items = list.filter(r => r.url).map(r => ({
              id: r.id, label: r.prompt || r.name || '图片', imageUrl: r.url, url: r.url,
              action: 'select',
            }));
          }
        } else if (nextLevel === 'variants') {
          const asset = item.asset;
          const variantCategory = item.category || prev.category || '';
          const assetName = asset.name || '主体';
          if (asset) {
            items = [{
              id: 'main', label: assetName, imageUrl: asset.imageUrl, url: asset.imageUrl,
              action: 'select', category: variantCategory,
              sourceType: variantCategory === 'character' ? 'characters' : variantCategory === 'scene' ? 'scenes' : variantCategory === 'prop' ? 'props' : variantCategory,
              sourceAssetId: asset.id,
              guaVideoAssetId: asset.guaVideoAssetId,
              guaVideoAssetStatus: asset.guaVideoAssetStatus,
              engGuaVideoAssetId: asset.engGuaVideoAssetId,
              engGuaVideoAssetStatus: asset.engGuaVideoAssetStatus,
              guaOminiAssetId: asset.guaOminiAssetId,
              guaOminiAssetStatus: asset.guaOminiAssetStatus,
              dreaminaProxyAssetId: asset.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: asset.dreaminaProxyAssetStatus,
              voiceSampleUrl: asset.voiceSampleUrl,
              voiceSampleName: asset.voiceSampleName,
              voiceBoundAt: asset.voiceBoundAt,
            }];
            (asset.variations || []).forEach((v, i) => {
              if (v.imageUrl) {
                items.push({
                  id: `var-${i}`, label: assetName, imageUrl: v.imageUrl, url: v.imageUrl,
                  action: 'select', category: variantCategory,
                  sourceType: variantCategory === 'character' ? 'characters' : variantCategory === 'scene' ? 'scenes' : variantCategory === 'prop' ? 'props' : variantCategory,
                  sourceAssetId: asset.id,
                  guaVideoAssetId: v.guaVideoAssetId,
                  guaVideoAssetStatus: v.guaVideoAssetStatus,
                  engGuaVideoAssetId: v.engGuaVideoAssetId,
                  engGuaVideoAssetStatus: v.engGuaVideoAssetStatus,
                  guaOminiAssetId: v.guaOminiAssetId,
                  guaOminiAssetStatus: v.guaOminiAssetStatus,
                  dreaminaProxyAssetId: v.dreaminaProxyAssetId,
                  dreaminaProxyAssetStatus: v.dreaminaProxyAssetStatus,
                  voiceSampleUrl: asset.voiceSampleUrl,
                  voiceSampleName: asset.voiceSampleName,
                  voiceBoundAt: asset.voiceBoundAt,
                });
              }
            });
          }
        }
        setVpMenuItems(items);
      } catch (err) {
        console.error('加载菜单失败:', err);
        setVpMenuItems([]);
      } finally {
        setVpMenuLoading(false);
      }
    }
  }, [vpMention, projectId, episodeId]);

  // ── @ 多级菜单：选中（去重 + 加入参考图）──
  const handleVpMenuSelect = useCallback((item) => {
    if (!item.url) return;

    // select-existing：已有参考图，只插入 chip，不添加到参考图列表
    if (item.action === 'select-existing') {
      handleVpSelect({ id: item.id, name: item.label, url: item.url, category: vpMention?.category || item.category || '' });
      return;
    }

    // 新参考图：去重添加 + 记录名字
    if (isGuaVideo) {
      const currentRefs = scene.video_gua_ref_images || [];
      if (!currentRefs.includes(item.url)) {
        handleChange(index, 'video_gua_ref_images', [...currentRefs, item.url]);
        handleChange(index, 'video_gua_ref_items', [
          ...(scene.video_gua_ref_items || []).filter((ref) => (ref.url || ref.imageUrl || ref.base64) !== item.url),
          {
            url: item.url,
            name: item.label || item.name,
            type: item.category,
            sourceType: item.sourceType,
            sourceAssetId: item.sourceAssetId,
            guaVideoAssetId: item.guaVideoAssetId,
            guaVideoAssetStatus: item.guaVideoAssetStatus,
            engGuaVideoAssetId: item.engGuaVideoAssetId,
            engGuaVideoAssetStatus: item.engGuaVideoAssetStatus,
            guaOminiAssetId: item.guaOminiAssetId,
            guaOminiAssetStatus: item.guaOminiAssetStatus,
            dreaminaProxyAssetId: item.dreaminaProxyAssetId,
            dreaminaProxyAssetStatus: item.dreaminaProxyAssetStatus,
            voiceSampleUrl: item.voiceSampleUrl,
            voiceSampleName: item.voiceSampleName,
            voiceBoundAt: item.voiceBoundAt,
          },
        ]);
        setVpRefNames(prev => new Map(prev).set(item.url, item.label));
      }
    }
    handleVpSelect({ id: item.id, name: item.label, url: item.url, category: vpMention?.category || item.category || '' });
  }, [isGuaVideo, scene.video_gua_ref_images, scene.video_gua_ref_items, index, handleChange, handleVpSelect, vpMention]);

  // ── @ 多级菜单：本地上传 ──
  const handleVpMentionUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadFileToCos(file);
      const name = file.name.split('.')[0] || '本地图片';
      if (isGuaVideo) {
        const currentRefs = scene.video_gua_ref_images || [];
        if (!currentRefs.includes(url)) {
          handleChange(index, 'video_gua_ref_images', [...currentRefs, url]);
          setVpRefNames(prev => new Map(prev).set(url, name));
        }
      }
      handleVpSelect({ id: Date.now(), name, url, category: 'upload' });
    } catch (err) {
      toast.error('上传失败: ' + err.message);
    } finally {
      e.target.value = '';
    }
  }, [isGuaVideo, scene.video_gua_ref_images, index, handleChange, handleVpSelect, toast]);

  // ── @ 多级菜单：返回上一级 ──
  const handleVpMenuBack = useCallback(() => {
    if (!vpMention || vpMention.path.length === 0) return;
    const newPath = [...vpMention.path];
    newPath.pop();
    if (newPath.length === 0) {
      setVpMention({ ...vpMention, level: 'root', path: [], searchText: '' });
      setVpMenuItems([]);
    } else {
      setVpMention({ ...vpMention, level: 'source', path: ['资源管理器'], searchText: '' });
      setVpMenuItems([
        { id: 'character', icon: Drama, label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
        { id: 'scene', icon: Mountain, label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
        { id: 'prop', icon: Wrench, label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
        { id: 'storyboard', icon: Clapperboard, label: '分镜', action: 'navigate', nextLevel: 'assets', category: 'storyboard' },
        { id: 'imageAgent', icon: Palette, label: '图片Agent', action: 'navigate', nextLevel: 'assets', category: 'imageAgent' },
      ]);
    }
    vpMentionIdxRef.current = 0;
  }, [vpMention]);

  const handleVpKeyDown = useCallback((e) => {
    const filtered = vpFilteredRef.current;
    if (vpMention && filtered.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); updateVpMentionIndex((vpMentionIdxRef.current + 1) % filtered.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); updateVpMentionIndex((vpMentionIdxRef.current - 1 + filtered.length) % filtered.length); return; }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[vpMentionIdxRef.current];
        if (!item) return;
        if (item.action === 'select' || item.action === 'select-existing') handleVpMenuSelect(item);
        else if (item.action === 'navigate' || item.action === 'upload') handleVpMenuNavigate(item);
        else handleVpSelect(item);
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (vpMention.path?.length > 0) handleVpMenuBack();
        else setVpMention(null);
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); setVpMention(null); return; }
    }
    if (e.key === 'Backspace') {
      const sel = window.getSelection();
      if (sel?.rangeCount > 0 && sel.isCollapsed) {
        const r = sel.getRangeAt(0), n = r.startContainer;
        if (n.nodeType === Node.TEXT_NODE && r.startOffset === 0 && n.previousSibling?.dataset?.mention) { e.preventDefault(); n.previousSibling.remove(); handleChange(index, 'video_prompt', getVpSerialized()); return; }
        if (n === vpEditorRef.current) { const ch = n.childNodes[r.startOffset - 1]; if (ch?.dataset?.mention) { e.preventDefault(); ch.remove(); handleChange(index, 'video_prompt', getVpSerialized()); return; } }
      }
    }
  }, [vpMention, updateVpMentionIndex, handleVpSelect, getVpSerialized, index, handleChange]);

  // 初始化 editor 内容 + 监听外部变化同步
  useEffect(() => {
    if (!vpEditorRef.current) return;
    if (vpEditingRef.current) {
      vpEditingRef.current = false;
      return;
    }
    const currentText = getVpSerialized();
    const externalText = scene.video_prompt || '';
    console.log('[ShotVisuals useEffect] currentText:', JSON.stringify(currentText), 'externalText:', JSON.stringify(externalText), 'match:', currentText === externalText);
    if (currentText !== externalText) {
      // 保存滚动位置，防止 DOM 操作触发浏览器重排导致滚动跳动
      const scrollParent = vpEditorRef.current.closest('.overflow-y-auto');
      const scrollTop = scrollParent?.scrollTop;
      renderPromptToEditor(vpEditorRef.current, externalText, 'var(--color-purple-primary)', true);
      // 恢复滚动位置
      if (scrollParent && scrollTop != null) {
        scrollParent.scrollTop = scrollTop;
      }
    }
  }, [scene.video_prompt, videoEnabled, getVpSerialized]);

  // 统一的容器样式
  const containerStyle = {
    aspectRatio,
    backgroundColor: theme.colors.bgOverlay,
  };

  // Sketch 特殊样式
  const sketchContainerStyle = {
    aspectRatio,
    backgroundColor: theme.colors.bgOverlay,
    borderColor: scene.sketch_preview ? 'transparent' : theme.colors.borderPrimary,
  };

  return (
    <div className={`flex-1 grid ${videoEnabled ? "grid-cols-3" : "grid-cols-2"} gap-3 w-full`}>
      {/* 1. Sketch */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <span className="text-2xs font-semibold px-2 py-0.5 rounded-md production-section-label" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>构思</span>
          </div>
          <button
            onClick={() => setShowSketchEditor(true)}
            className="production-icon-button flex items-center justify-center"
            style={{ color: theme.colors.textMuted }}
            title="绘制草图"
          >
            <Pencil size={12} />
          </button>
        </div>
        <div
          className={`w-full production-media-frame border-dashed flex flex-col items-center justify-center overflow-hidden relative transition-colors cursor-pointer ${scene.sketch_preview ? "border-transparent" : "min-h-[120px]"}`}
          style={sketchContainerStyle}
          onClick={() => !scene.sketch_preview && sketchInputRef.current.click()}
        >
          <input type="file" accept="image/png, image/jpeg" className="hidden" ref={sketchInputRef} onChange={(e) => handleSketchUpload(index, e.target.files[0])} />
          {scene.sketch_preview ? (
            <>
              <img src={scene.sketch_preview} className="w-full h-full object-cover" />
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowSketchEditor(true); }}
                  className="p-1.5 rounded-md"
                  style={{ backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-primary)' }}
                  title="重新绘制"
                >
                  <Pencil size={12} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleChange(index, "sketch_preview", null); handleChange(index, "sketch_base64", null); }}
                  className="p-1.5 rounded-md"
                  style={{ backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-primary)' }}
                  title="删除草图"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2" style={{ color: theme.colors.textMuted }}>
              <div className="flex gap-3">
                <button
                  onClick={(e) => { e.stopPropagation(); sketchInputRef.current.click(); }}
                  className="flex flex-col items-center gap-1 p-2 rounded-md transition-colors hover:bg-[var(--color-bg-tertiary)]"
                >
                  <div className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}><Upload size={12} /></div>
                  <span className="text-2xs">上传</span>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowSketchEditor(true); }}
                  className="flex flex-col items-center gap-1 p-2 rounded-md transition-colors hover:bg-[var(--color-bg-tertiary)]"
                >
                  <div className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}><Pencil size={12} /></div>
                  <span className="text-2xs">绘制</span>
                </button>
              </div>
            </div>
          )}
        </div>
        {ReactDOM.createPortal(
          <SketchEditorModal
            isOpen={showSketchEditor}
            aspectRatio={currentRatio || '16:9'}
            onSave={(cosUrl) => {
              handleChange(index, "sketch_preview", cosUrl);
              handleChange(index, "sketch_base64", cosUrl);
              setShowSketchEditor(false);
            }}
            onClose={() => setShowSketchEditor(false)}
            title={`构思草图 #${index + 1}`}
          />,
          document.body
        )}
      </div>

      {/* 2. Render - 图片区域 */}
      <div className="flex flex-col gap-1.5 relative">
        {/* 标题栏：渲染 + 所有工具按钮 */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <span className="text-2xs font-semibold px-2 py-0.5 rounded-md" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-tertiary)' }}>渲染</span>
          </div>
          <div className="flex items-center gap-1">
            {/* 候选图切换标签 */}
            {scene.image_candidates?.length > 1 && (
              <button
                onClick={() => setShowCandidates(v => !v)}
                className="px-1.5 py-0.5 rounded-md text-2xs font-medium transition-all duration-200"
                style={{
                  backgroundColor: showCandidates ? theme.colors.accentPrimary : 'var(--color-bg-tertiary)',
                  color: showCandidates ? 'white' : theme.colors.textMuted,
                }}
                onMouseEnter={(e) => { if (!showCandidates) { e.currentTarget.style.backgroundColor = `${theme.colors.accentPrimary}30`; e.currentTarget.style.color = theme.colors.accentPrimary; }}}
                onMouseLeave={(e) => { if (!showCandidates) { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = theme.colors.textMuted; }}}
                title="查看候选图"
              >
                {scene.image_candidates.length}张
              </button>
            )}
            {/* 重新生成1张 */}
            {scene.generated_image && !scene.isGenerating && (
              <IconBtn onClick={() => handleGenerateImage(index)} color={theme.colors.bluePrimary} label="重新生成1张">
                <RefreshCw size={11} />
              </IconBtn>
            )}
            {/* 重新生成4张 */}
            {scene.generated_image && !scene.isGenerating && (
              <IconBtn onClick={() => handleGenerateImageBatch?.(index)} color={theme.colors.purplePrimary} label="重新生成4张">
                <Grid3X3 size={11} />
              </IconBtn>
            )}
            {/* 上传 */}
            {!scene.isGenerating && (
              <IconBtn onClick={() => imageUploadRef.current.click()} color={theme.colors.textMuted} label="上传图片">
                <Upload size={11} />
              </IconBtn>
            )}
            {/* 资源库 */}
            {!scene.isGenerating && (
              <IconBtn onClick={() => setShowResourceSelector(true)} color={theme.colors.textMuted} label="资源库">
                <FolderOpen size={11} />
              </IconBtn>
            )}
            {/* 下载 */}
            {scene.generated_image && !scene.isGenerating && (
              <IconBtn onClick={handleDownloadImage} color={theme.colors.textMuted} label="下载图片">
                <Download size={11} />
              </IconBtn>
            )}
            {/* 超分 */}
            {scene.generated_image && !scene.isGenerating && (
              <IconBtn onClick={handleUpscale} disabled={isUpscaling} color={theme.colors.textMuted} activeColor={theme.colors.accentPrimary} active={isUpscaling} label="超分4K">
                {isUpscaling ? <Loader2 size={11} className="animate-spin" /> : <ZoomIn size={11} />}
              </IconBtn>
            )}
            {/* 打码 */}
            {scene.generated_image && !scene.isGenerating && (
              <IconBtn onClick={() => setShowMosaicEditor(true)} color={theme.colors.textMuted} label="打码">
                <SquareDashed size={11} />
              </IconBtn>
            )}
            {/* 工作站 */}
            {scene.generated_image && !scene.isGenerating && (
              <IconBtn onClick={() => onOpenWorkstation(scene.generated_image)} color={theme.colors.textMuted} label="工作站">
                <Maximize2 size={11} />
              </IconBtn>
            )}
            {/* 历史记录 */}
            <IconBtn onClick={() => setShowImageHistory(!showImageHistory)} color={theme.colors.textMuted} activeColor={theme.colors.accentPrimary} active={showImageHistory} label="历史记录">
              <History size={11} />
            </IconBtn>
          </div>
        </div>

        {imageHistoryAnim.mounted && (
          <div className="absolute top-0 right-0 z-50 mt-6">
            <HistoryPanel
              historyList={scene.history_list}
              type="image"
              onRestore={handleRestore}
              onClose={() => setShowImageHistory(false)}
              exiting={imageHistoryAnim.exiting}
            />
          </div>
        )}

        {/* 图片容器 */}
        <div
          className={`w-full production-media-frame overflow-hidden relative ${!scene.generated_image ? "min-h-[120px]" : ""}`}
          style={containerStyle}
        >
          <input type="file" accept="image/*" className="hidden" ref={imageUploadRef} onChange={(e) => handleLocalImageUpload(index, e.target.files[0])} />
          {scene.isGenerating && !scene.generated_image ? (
            <div
              className="w-full h-full flex flex-col items-center justify-center gap-2 relative overflow-hidden"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              {/* 扫光动画背景 */}
              <div
                className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite]"
                style={{
                  background: `linear-gradient(90deg, transparent, ${theme.colors.accentPrimary}15, transparent)`,
                }}
              />
              <Wand2 className="animate-spin relative z-10" size={18} style={{ color: theme.colors.accentPrimary }} />
              <span className="text-2xs font-medium relative z-10" style={{ color: theme.colors.accentPrimary }}>
                {scene.image_candidates?.length > 0 ? `已生成 ${scene.image_candidates.length}/4` : '生成中...'}
              </span>
              {/* 进度条 */}
              <div className="relative z-10 w-3/4 h-0.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-border-primary)' }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${scene.image_candidates?.length > 0 ? (scene.image_candidates.length / 4) * 100 : 15}%`,
                    backgroundColor: theme.colors.accentPrimary,
                  }}
                />
              </div>
              <button
                onClick={() => stopImageGeneration(index)}
                className="relative z-10 px-2 py-0.5 rounded text-2xs transition-all duration-200"
                style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-error)'; e.currentTarget.style.color = 'white'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-red-light)'; e.currentTarget.style.color = 'var(--color-error)'; }}
              >
                停止
              </button>
            </div>
          ) : scene.generated_image ? (
            <div className="ui-generation-result-enter w-full h-full relative">
              <img src={scene.generated_image} className="w-full h-full object-cover" />

              {/* 重新生成时的半透明遮罩 Loading */}
              {scene.isGenerating && (
                <div
                  className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--overlay-medium)', backdropFilter: 'blur(4px)' }}
                >
                  <Wand2 className="animate-spin" size={20} style={{ color: theme.colors.accentPrimary }} />
                  <span className="text-xs font-medium" style={{ color: 'white' }}>
                    {scene.image_candidates?.length > 0 ? `生成中 ${scene.image_candidates.length}/4` : '重新生成中...'}
                  </span>
                  {/* 进度轨道：深色媒体遮罩上的浅色轨道，与主题无关，保留 */}
                  <div className="w-24 h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(scene.image_candidates?.length || 0) * 25}%`,
                        backgroundColor: theme.colors.accentPrimary
                      }}
                    />
                  </div>
                  <button
                    onClick={() => stopImageGeneration(index)}
                    className="px-2 py-1 rounded text-2xs font-medium transition-all duration-200"
                    style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-error)'; e.currentTarget.style.color = 'white'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-red-light)'; e.currentTarget.style.color = 'var(--color-error)'; }}
                  >
                    停止生成
                  </button>
                </div>
              )}

              {/* 候选图浮层 */}
              {showCandidates && scene.image_candidates?.length > 1 && (
                <div
                  className="absolute inset-x-0 bottom-0 z-20 p-2"
                  style={{ backgroundColor: 'var(--overlay-heavy)' }}
                >
                  <div className="flex gap-1.5 justify-center">
                    {scene.image_candidates.map((url, i) => {
                      const isActive = url === scene.generated_image;
                      return (
                        <button
                          key={i}
                          onClick={() => replaceGeneratedImage(url, {
                            source: 'candidate',
                            model: 'candidate',
                            addHistory: false,
                            ensureCandidate: false,
                          })}
                          className="relative shrink-0 rounded overflow-hidden transition-all hover:scale-105"
                          style={{
                            width: 40,
                            height: 40,
                            border: `2px solid ${isActive ? theme.colors.accentPrimary : 'transparent'}`,
                            boxShadow: isActive ? `0 0 0 1px ${theme.colors.accentPrimary}` : undefined,
                          }}
                          title={`选择第 ${i + 1} 张`}
                        >
                          <img src={url} className="w-full h-full object-cover" />
                          {isActive && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 flex items-center justify-center rounded-tl"
                              style={{ backgroundColor: theme.colors.accentPrimary }}
                            >
                              <svg width="6" height="6" viewBox="0 0 8 8" fill="none">
                                <path d="M1 4l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 cursor-pointer transition-all" style={{ backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-muted)' }}>
              <div onClick={() => handleGenerateImage(index)} className="flex flex-col items-center gap-2">
                <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}><Wand2 size={20} /></div>
                <span className="text-2xs font-medium">点击生成</span>
              </div>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => handleGenerateImageBatch?.(index)}
                  className="px-2 py-1 rounded-md text-2xs flex items-center gap-1 transition-colors duration-200"
                  style={{ backgroundColor: 'var(--color-purple-light)', color: 'var(--color-purple-primary)', border: '1px solid var(--color-border-primary)' }}
                >
                  <Grid3X3 size={10} /> 生成4张
                </button>
                <button
                  onClick={() => setShowResourceSelector(true)}
                  className="px-2 py-1 rounded-md text-2xs flex items-center gap-1 transition-colors duration-200"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)'; e.currentTarget.style.color = 'var(--color-text-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
                >
                  <FolderOpen size={10} /> 资源库
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 尾帧缩略图 */}
      {videoEnabled && visibleLastFrameImage && !scene.isGeneratingVideo && (
        <div className="flex items-center gap-2 px-1">
          <span className="text-2xs font-bold" style={{ color: 'var(--color-text-muted)' }}>尾帧</span>
          <div
            className="w-16 h-9 rounded overflow-hidden flex-shrink-0"
            style={{ border: '1px solid var(--color-border-primary)' }}
          >
            <img src={visibleLastFrameImage} className="w-full h-full object-cover" alt="尾帧" />
          </div>
        </div>
      )}

      {/* 3. Video - 视频区域 */}
      {videoEnabled && (
      <div className="flex flex-col gap-1.5 relative">
        {/* 标题行：模型选择器（替代"动态"标签）+ 工具按钮 */}
        <div className="flex items-center justify-between px-1">
          <div
            ref={modelBtnRef}
            onClick={() => setOpenVideoSettingsIndex(openVideoSettingsIndex === index ? null : index)}
            className="flex items-center gap-1.5 px-2 py-0.5 rounded-md cursor-pointer transition-colors"
            style={{
              backgroundColor: openVideoSettingsIndex === index ? (currentModel.accentColor || theme.colors.bluePrimary) : `${currentModel.accentColor || theme.colors.bluePrimary}15`,
              border: `1.5px solid ${openVideoSettingsIndex === index ? (currentModel.accentColor || theme.colors.bluePrimary) : `${currentModel.accentColor || theme.colors.bluePrimary}40`}`,
            }}
          >
            <currentModel.icon size={11} style={{ color: openVideoSettingsIndex === index ? 'white' : (currentModel.accentColor || theme.colors.bluePrimary) }} />
            <span className="text-2xs font-bold" style={{ color: openVideoSettingsIndex === index ? 'white' : (currentModel.accentColor || theme.colors.bluePrimary) }}>{currentModel.name}</span>
            <span
              className="text-2xs px-1 py-px rounded leading-none font-bold"
              style={{
                backgroundColor: openVideoSettingsIndex === index ? 'color-mix(in srgb, var(--color-accent-contrast) 25%, transparent)' : `${currentModel.accentColor || theme.colors.bluePrimary}20`,
                color: openVideoSettingsIndex === index ? 'white' : (currentModel.accentColor || theme.colors.bluePrimary),
              }}
            >{currentModel.tag}</span>
            <Settings2 size={10} style={{ color: openVideoSettingsIndex === index ? 'color-mix(in srgb, var(--color-accent-contrast) 70%, transparent)' : `${currentModel.accentColor || theme.colors.bluePrimary}60` }} />
          </div>
          <div className="flex gap-1 items-center shrink-0">
            {scene.generated_video && !scene.isGeneratingVideo && (
              <IconBtn onClick={() => handleGenerateVideo(index)} color={theme.colors.bluePrimary} label="重新生成视频"><RefreshCw size={11} /></IconBtn>
            )}
            {scene.generated_video && !scene.isGeneratingVideo && (
              <IconBtn onClick={() => handleChange(index, "isEditingVideoPrompt", true)} color={theme.colors.accentPrimary} label="编辑提示词"><PenTool size={11} /></IconBtn>
            )}
            {!scene.isGeneratingVideo && (
              <IconBtn onClick={() => setShowVideoResourceSelector(true)} color={theme.colors.textMuted} label="资源库"><FolderOpen size={11} /></IconBtn>
            )}
            {scene.generated_video && !scene.isGeneratingVideo && (
              <IconBtn onClick={() => videoUploadRef.current.click()} color={theme.colors.textMuted} label="上传视频"><Upload size={11} /></IconBtn>
            )}
            {scene.generated_video && !scene.isGeneratingVideo && (
              <IconBtn onClick={handleDownloadVideo} color={theme.colors.success} label="下载视频"><Download size={11} /></IconBtn>
            )}
            {scene.generated_video && !scene.isGeneratingVideo && (
              <IconBtn onClick={() => setShowFrameCapture(true)} color={'var(--color-warning)'} label="截取帧"><Scissors size={11} /></IconBtn>
            )}
            {scene.generated_video && !scene.isGeneratingVideo && (
              isErasingSubtitle
                ? <span className="flex items-center gap-1 px-1 text-2xs" style={{ color: 'var(--color-error)' }}><Loader2 size={11} className="animate-spin" /></span>
                : <IconBtn onClick={handleEraseSubtitle} color="var(--color-error)" label="去字幕（仅限GuaVideo 24小时内生成视频）"><RemoveFormatting size={11} /></IconBtn>
            )}
            <IconBtn onClick={() => setShowVideoHistory(!showVideoHistory)} color={theme.colors.textMuted} activeColor={theme.colors.accentPrimary} active={showVideoHistory} label="历史记录"><History size={11} /></IconBtn>
            <IconBtn onClick={() => handleAutoVideoPrompt(index)} disabled={scene.isGeneratingPrompt} color={theme.colors.accentPrimary} label="AI写提示词">
              {scene.isGeneratingPrompt ? <Loader2 size={11} className="animate-spin" /> : <Lightbulb size={11} />}
            </IconBtn>
          </div>
        </div>

        {videoHistoryAnim.mounted && (
            <div className="absolute top-0 right-0 z-50 mt-2">
              <HistoryPanel
                historyList={scene.history_list}
                type="video"
                onRestore={handleRestore}
                onClose={() => setShowVideoHistory(false)}
                exiting={videoHistoryAnim.exiting}
              />
            </div>
        )}

        {/* 视频容器 */}
        <div
          className={`w-full production-media-frame overflow-hidden relative ${!scene.generated_video ? "min-h-[120px]" : ""}`}
          style={{ ...containerStyle, borderColor: theme.colors.borderSecondary }}
        >
          {/* VideoSettingsPanel - 侧滑抽屉（自带 Portal） */}
          {videoSettingsAnim.mounted && (
            <VideoSettingsPanel
              scene={scene}
              index={index}
              rows={rows}
              onClose={() => setOpenVideoSettingsIndex(null)}
              handleChange={handleChange}
              onOpenMultiShot={onOpenMultiShot}
              characterOptions={characterOptions}
              sceneOptions={sceneOptions}
              propOptions={propOptions}
              handleGenerateVideo={handleGenerateVideo}
              projectId={projectId}
              episodeId={episodeId}
              exiting={videoSettingsAnim.exiting}
            />
          )}

          <input type="file" accept="video/*" className="hidden" ref={videoUploadRef} onChange={(e) => handleLocalVideoUpload(index, e.target.files[0])} />
          {scene.isGeneratingVideo && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2" style={{ backgroundColor: 'var(--color-bg-overlay)', color: theme.colors.accentPrimary }}>
              <Loader2 size={18} className="animate-spin" /><span className="text-2xs animate-pulse">制作中...</span>
              {canCancelVideoTask && (
                <button
                  onClick={() => stopVideoGeneration(index)}
                  className="px-3 py-1 rounded text-2xs transition-all duration-200"
                  style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-error)'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-red-light)'; e.currentTarget.style.color = 'var(--color-error)'; }}
                >
                  停止
                </button>
              )}
            </div>
          )}
          {!scene.isGeneratingVideo && scene.video_task_status === 'failed' && scene.video_task_error && (
            <div
              className="absolute left-2 right-2 bottom-2 z-30 px-2 py-1 rounded text-2xs truncate"
              style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)', border: '1px solid var(--color-error)' }}
              title={scene.video_task_error}
            >
              视频生成失败：{scene.video_task_error}
            </div>
          )}
          {scene.generated_video && !scene.isEditingVideoPrompt ? (
            <div className="ui-generation-result-enter w-full h-full relative">
              {/* 视频信箱黑边：媒体内容底色，纯黑与主题无关，保留 */}
              <video key={scene.generated_video} src={scene.generated_video} poster={scene.generated_image || undefined} className="w-full h-full object-contain" style={{ backgroundColor: '#000' }} muted preload="none" controls />
              <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-2xs" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-secondary)' }}>{currentModel.name.split(" ")[0]}</div>
            </div>
          ) : null}
          {(!scene.generated_video || scene.isEditingVideoPrompt) ? (
            <div className="w-full h-full flex flex-col" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
              {scene.isEditingVideoPrompt && (
                <button
                  onClick={() => handleChange(index, "isEditingVideoPrompt", false)}
                  className="absolute top-1 right-1 z-20 p-1 rounded transition-colors hover:bg-black/50"
                  style={{ backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-muted)' }}
                  title="返回视频"
                  aria-label="返回视频"
                >
                  <X size={10} />
                </button>
              )}
              {/* GuaVideo ref2v/t2v 模式提示 */}
              {isGuaVideo && (scene.video_gua_mode === "ref2v" || scene.video_gua_mode === "t2v") && !scene.isEditingVideoPrompt && (
                <div className="px-2 py-1 text-center" style={{ backgroundColor: '#8b5cf615' }}>
                  <span className="text-2xs font-medium" style={{ color: 'var(--color-purple-primary)' }}>
                    {scene.video_gua_mode === "ref2v" ? "参考图生视频 — 无需先生成图片" : "文生视频 — 直接输入提示词"}
                  </span>
                </div>
              )}
              <div className="relative flex-1 flex flex-col min-h-0">
                <style>{`[data-placeholder]:empty:before { content: attr(data-placeholder); color: var(--color-text-muted); pointer-events: none; }`}</style>
                <div
                  ref={vpEditorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleVpInput}
                  onKeyDown={handleVpKeyDown}
                  onPaste={(e) => { const html = e.clipboardData.getData('text/html'); if (html && html.includes('data-mention')) return; e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
                  data-placeholder="视频提示词...输入 @ 引用参考图"
                  className="flex-1 bg-transparent p-2 text-2xs font-mono outline-none whitespace-pre-wrap break-words overflow-y-auto"
                  style={{ color: theme.colors.textPrimary, opacity: 0.7 }}
                />
                {vpMenuAnim.mounted && (vpMention || lastVpMentionRef.current) && ReactDOM.createPortal(
                  <div className="fixed inset-0 z-[300]" onMouseDown={() => setVpMention(null)}>
                    <div
                      ref={vpMentionListRef}
                      className={`fixed w-56 rounded-lg shadow-2xl py-1.5 px-1 max-h-64 overflow-y-auto ${vpMenuAnim.exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-150"}`}
                      style={{
                        ...((() => {
                          const sel = window.getSelection();
                          let cursorRect = null;
                          if (sel?.rangeCount > 0) {
                            const range = sel.getRangeAt(0);
                            const rects = range.getClientRects();
                            cursorRect = rects.length > 0 ? rects[0] : range.getBoundingClientRect();
                          }
                          const fallback = vpEditorRef.current?.getBoundingClientRect();
                          const bottom = cursorRect?.bottom || fallback?.bottom || 0;
                          const top = cursorRect?.top || fallback?.top || 0;
                          let left = cursorRect?.left || fallback?.left || 0;
                          const menuW = 228;
                          if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;
                          if (left < 8) left = 8;
                          if (top < 300) {
                            return { top: bottom + 4, left };
                          }
                          return { bottom: window.innerHeight - top + 4, left };
                        })()),
                        backgroundColor: 'color-mix(in srgb, var(--color-bg-secondary) 92%, transparent)',
                        backdropFilter: 'blur(20px) saturate(1.5)',
                        WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
                        border: '1px solid var(--color-border-primary)',
                      }}
                      onMouseDown={e => e.stopPropagation()}
                    >
                      {/* 面包屑 */}
                      {(vpMention || lastVpMentionRef.current)?.path?.length > 0 && (
                        <div className="flex items-center gap-1 px-2.5 py-1 mb-1" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                          <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleVpMenuBack(); }} className="text-2xs hover:underline" style={{ color: 'var(--color-text-muted)' }}>←</button>
                          {(vpMention || lastVpMentionRef.current).path.map((p, i) => (
                            <span key={i} className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{i > 0 && ' › '}{p}</span>
                          ))}
                        </div>
                      )}

                      {vpMenuLoading && (
                        <div className="flex items-center justify-center py-3">
                          <Loader2 size={14} className="animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                        </div>
                      )}

                      {!vpMenuLoading && vpFiltered.map((item, i) => (
                        <button
                          key={item.id}
                          ref={el => vpMentionItemRefs.current[i] = el}
                          data-image-url={item.imageUrl || ''}
                          onMouseDown={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            setVpPreviewUrl(null);
                            if (item.action === 'select' || item.action === 'select-existing') handleVpMenuSelect(item);
                            else handleVpMenuNavigate(item);
                          }}
                          onMouseEnter={() => updateVpMentionIndex(i)}
                          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-2xs text-left transition-all"
                          style={{
                            color: i === 0 && vpMentionIdxRef.current === 0 ? 'var(--color-purple-primary)' : 'var(--color-text-secondary)',
                            backgroundColor: i === 0 && vpMentionIdxRef.current === 0 ? 'var(--color-purple-light)' : 'transparent',
                          }}
                        >
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }} />
                          ) : item.icon ? (
                            <span className="w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ color: 'var(--color-text-muted)' }}>
                              {(() => { const MenuIcon = item.icon; return <MenuIcon size={14} />; })()}
                            </span>
                          ) : null}
                          <span className="truncate flex-1">{item.label || item.name}</span>
                          {item.action === 'navigate' && <span style={{ color: 'var(--color-text-muted)' }}>›</span>}
                        </button>
                      ))}

                      {!vpMenuLoading && vpFiltered.length === 0 && (
                        <div className="px-2.5 py-3 text-center text-2xs" style={{ color: 'var(--color-text-muted)' }}>暂无内容</div>
                      )}
                    </div>
                  </div>,
                  document.body
                )}

                {/* @ 本地上传隐藏 input */}
                <input type="file" ref={vpMentionUploadRef} className="hidden" accept="image/*" onChange={handleVpMentionUpload} />
              </div>
              <div className="flex gap-1 p-1">
                {scene.isGeneratingVideo && canCancelVideoTask ? (
                  <button
                    onClick={() => stopVideoGeneration(index)}
                    className="flex-1 h-7 text-2xs font-medium flex items-center justify-center gap-1 rounded transition-all duration-200"
                    style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-error)'; e.currentTarget.style.color = 'white'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-red-light)'; e.currentTarget.style.color = 'var(--color-error)'; }}
                  >
                    <X size={10} /> 停止
                  </button>
                ) : !scene.isGeneratingVideo ? (
                  <button
                    onClick={() => { handleGenerateVideo(index); handleChange(index, "isEditingVideoPrompt", false); }}
                    disabled={scene.isGeneratingVideo}
                    className="flex-1 h-7 text-2xs font-medium flex items-center justify-center gap-1 rounded transition-all duration-200"
                    style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                    onMouseEnter={(e) => { if (!scene.isGeneratingVideo) { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.boxShadow = `0 2px 8px ${theme.colors.accentPrimary}40`; }}}
                    onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <Play size={10} />
                    生成
                  </button>
                ) : (
                  <span className="flex-1 h-7 text-2xs flex items-center justify-center" style={{ color: 'var(--color-text-muted)' }}>
                    厂商任务生成中
                  </span>
                )}
                <button
                  onClick={() => setShowVideoResourceSelector(true)}
                  className="h-7 px-2 rounded text-2xs flex items-center gap-1"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
                  title="从资源库选择"
                >
                  <FolderOpen size={12} />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      )}

      {/* 资源选择器 - 使用 Portal 渲染到 body 避免裁剪问题 */}
      {showResourceSelector && (
        <ResourceSelector
          isOpen={showResourceSelector}
          onClose={() => setShowResourceSelector(false)}
          onSelect={handleSelectResource}
          projectId={projectId}
          type="image"
          title="选择图片资源"
        />
      )}
      
      {/* 视频资源选择器 */}
      {videoEnabled && showVideoResourceSelector && (
        <ResourceSelector
          isOpen={showVideoResourceSelector}
          onClose={() => setShowVideoResourceSelector(false)}
          onSelect={handleSelectResource}
          projectId={projectId}
          type="video"
          title="选择视频资源"
        />
      )}

      {/* @ 菜单选中项大图预览（独立组件，不触发父组件重渲染） */}
      {videoEnabled && <VpMentionPreview url={vpPreviewUrl} anchorRect={vpPreviewRect} />}

      {/* 视频帧截取弹窗 */}
      {videoEnabled && frameCaptureAnim.mounted && scene.generated_video && (
        <FrameCaptureModal
          videoUrl={scene.generated_video}
          exiting={frameCaptureAnim.exiting}
          onConfirm={(cosUrl) => {
            handleInsertRowAndSave(index + 1, { generated_image: cosUrl });
            setShowFrameCapture(false);
            toast.success("已截取帧并插入新分镜");
          }}
          onClose={() => setShowFrameCapture(false)}
        />
      )}

      {/* 打码编辑弹窗 */}
      {mosaicEditorAnim.mounted && scene.generated_image && (
        <MosaicEditorModal
          imageUrl={scene.generated_image}
          exiting={mosaicEditorAnim.exiting}
          onConfirm={(cosUrl) => {
            replaceGeneratedImage(cosUrl, {
              oldUrl: scene.generated_image,
              source: "mosaic",
              model: "mosaic-editor",
            });
            setShowMosaicEditor(false);
            toast.success("打码完成");
          }}
          onClose={() => setShowMosaicEditor(false)}
        />
      )}
    </div>
  );
};

export default ShotVisuals;
